// Global Instructions Rule Applied!
//
// Cap table models — the whole-view payload from GET /deal-execution/cap-table
// (view__cap_table): version history, selected snapshot, headline tiles (with
// prior-version tiles for deltas), class lines with sparse holder detail and
// our per-fund rows DERIVED from asset deltas (never typed). Kept separate
// from deal_execution_board_model.ts (same isolation rationale).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const CapTableVersionListEntrySchema = z
  .object({
    cap_table_versions__id: z.string(),
    cap_table_versions__kind: z.string(),
    cap_table_versions__status: z.string(),
    cap_table_versions__as_of_date: z.string(),
    cap_table_versions__adjustment_factor: z.number().nullish(),
    cap_table_versions__notes: z.string().nullish(),
    cap_table_versions__date_modified: z.string().nullish(),
    rounds__id: z.string().nullish(),
    rounds__name: z.string().nullish(),
    prog__files_count: z.number().nullish()
  })
  .strict()

export type CapTableVersionListEntry = z.infer<typeof CapTableVersionListEntrySchema>

export const CapTableVersionHeaderSchema = z
  .object({
    cap_table_versions__id: z.string(),
    cap_table_versions__kind: z.string(),
    cap_table_versions__status: z.string(),
    cap_table_versions__as_of_date: z.string(),
    cap_table_versions__adjustment_factor: z.number().nullish(),
    cap_table_versions__notes: z.string().nullish(),
    rounds__id: z.string().nullish(),
    rounds__name: z.string().nullish(),
    rounds__issued_shares: z.number().nullish(),
    rounds__fully_diluted_shares: z.number().nullish()
  })
  .strict()

export type CapTableVersionHeader = z.infer<typeof CapTableVersionHeaderSchema>

export const CapTableTilesSchema = z
  .object({
    issued_total: z.number().nullish(),
    fd_total: z.number().nullish(),
    esop_reserve: z.number().nullish(),
    our_units: z.number().nullish(),
    our_pct_fd: z.number().nullish()
  })
  .strict()

export type CapTableTiles = z.infer<typeof CapTableTilesSchema>

export const CapTableOurRowSchema = z
  .object({
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    prog__units: z.number().nullish()
  })
  .strict()

export type CapTableOurRow = z.infer<typeof CapTableOurRowSchema>

export const CapTableHolderSchema = z
  .object({
    cap_table_holdings__id: z.string(),
    cap_table_holdings__holder_kind: z.string(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    cap_table_holdings__holder_name: z.string().nullish(),
    cap_table_holdings__issued: z.number().nullish(),
    cap_table_holdings__fully_diluted: z.number().nullish(),
    cap_table_holdings__sort_order: z.number().nullish()
  })
  .strict()

export type CapTableHolder = z.infer<typeof CapTableHolderSchema>

export const CapTableLineSchema = z
  .object({
    cap_table_lines__id: z.string(),
    cap_table_lines__class_name: z.string(),
    // Many-to-many: all securities mapped to this class line.
    securities: z.array(
      z
        .object({
          securities__id: z.string(),
          securities__name: z.string().nullish()
        })
        .strict()
    ),
    cap_table_lines__issued: z.number().nullish(),
    cap_table_lines__fully_diluted: z.number().nullish(),
    cap_table_lines__issue_price: z.number().nullish(),
    cap_table_lines__is_esop_pool: z.boolean().nullish(),
    cap_table_lines__line_kind: z.string(),
    cap_table_lines__sort_order: z.number().nullish(),
    prog__issued_pct: z.number().nullish(),
    prog__fd_pct: z.number().nullish(),
    prog__our_units: z.number().nullish(),
    prog__our_pct_of_class: z.number().nullish(),
    prog__our_rows: z.array(CapTableOurRowSchema),
    holders: z.array(CapTableHolderSchema)
  })
  .strict()

export type CapTableLine = z.infer<typeof CapTableLineSchema>

export const CapTableUnmatchedRowSchema = z
  .object({
    securities__id: z.string().nullish(),
    securities__name: z.string().nullish(),
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    prog__units: z.number().nullish()
  })
  .strict()

export type CapTableUnmatchedRow = z.infer<typeof CapTableUnmatchedRowSchema>

export const CapTableCompanySecuritySchema = z
  .object({
    securities__id: z.string(),
    securities__name: z.string().nullish(),
    // cfg.security_types.default_line_kind — the line kind the editor
    // adopts when a line gets its first linked security of this type.
    security_types__default_line_kind: z.string().nullish()
  })
  .strict()

export type CapTableCompanySecurity = z.infer<typeof CapTableCompanySecuritySchema>

export const CapTableCompanyRoundSchema = z
  .object({
    rounds__id: z.string(),
    rounds__name: z.string().nullish(),
    rounds__round_close_date: z.string().nullish()
  })
  .strict()

export type CapTableCompanyRound = z.infer<typeof CapTableCompanyRoundSchema>

export const StaleSplitSchema = z
  .object({
    asset_transactions__transaction_date: z.string(),
    asset_transactions__split_factor: z.number().nullish()
  })
  .strict()

export type StaleSplit = z.infer<typeof StaleSplitSchema>

export const CapTableRoundParticipationSchema = z
  .object({
    round_participations__id: z.string(),
    rounds__id: z.string(),
    round_participations__participant_kind: z.string(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    round_participations__name: z.string().nullish(),
    round_participations__amount: z.number().nullish()
  })
  .strict()

export type CapTableRoundParticipation = z.infer<typeof CapTableRoundParticipationSchema>

export const CapTableDataSchema = z
  .object({
    // Date our holdings are measured at (today unless time-travelling).
    measured_at: z.string().nullish(),
    // Booked splits dated after the displayed version's as-of date.
    stale_splits: z.array(StaleSplitSchema),
    versions: z.array(CapTableVersionListEntrySchema),
    version: CapTableVersionHeaderSchema.nullish(),
    tiles: CapTableTilesSchema.nullish(),
    prior_tiles: CapTableTilesSchema.nullish(),
    lines: z.array(CapTableLineSchema),
    our_unmatched: z.array(CapTableUnmatchedRowSchema),
    company_securities: z.array(CapTableCompanySecuritySchema),
    company_rounds: z.array(CapTableCompanyRoundSchema),
    // Round co-investor participations (all company rounds) — the editor's
    // import-as-holders prefill.
    round_participations: z.array(CapTableRoundParticipationSchema)
  })
  .strict()

export type CapTableData = z.infer<typeof CapTableDataSchema>

export function validateCapTableData(data: unknown): CapTableData {
  try {
    return validateWithZodSchema(CapTableDataSchema, data, 'cap table') as CapTableData
  } catch (e) {
    throw new Error(`Cap table validation failed: ${e}`)
  }
}
